import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2, UserCheck } from "lucide-react";
import { api } from "@/auth/AuthContext";

const fmtDate = (iso) => {
  try { return new Date(`${iso}T00:00:00`).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch (_) { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return "-";
  try { return `${new Date(iso).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" })} WIB`; }
  catch (_) { return "-"; }
};

const DocCell = ({ isLogin, url, testid }) => {
  if (isLogin) return <span className="laksmi-doc-login" data-testid={testid}>login</span>;
  if (!url) return <span className="laksmi-doc-empty" data-testid={testid}>-</span>;
  return (
    <a className="laksmi-doc-link" href={url} target="_blank" rel="noopener noreferrer" data-testid={testid}>
      <ExternalLink size={12} /> Lihat
    </a>
  );
};

const DokumentasiCell = ({ guest }) => {
  const [link, setLink] = useState(guest.dokumentasi_link || "");
  const [editing, setEditing] = useState(!guest.dokumentasi_link);
  const [draft, setDraft] = useState(guest.dokumentasi_link || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (saving) return;
    setSaving(true); setErr("");
    try {
      const res = await api.post(`/laksmi/admin/guests/${guest.guest_id}/dokumentasi`, { url: draft.trim() });
      const saved = res.data.dokumentasi_link || "";
      setLink(saved); setEditing(!saved);
    } catch (e) {
      setErr(e.response?.data?.detail || "Tautan tidak dapat disimpan.");
    } finally { setSaving(false); }
  };

  if (!editing && link) {
    return (
      <a className="laksmi-doc-link" href={link} target="_blank" rel="noopener noreferrer"
         data-testid={`laksmi-history-dok-${guest.guest_id}`} title="Klik kanan untuk mengedit"
         onContextMenu={(e) => { e.preventDefault(); setDraft(link); setErr(""); setEditing(true); }}>
        <ExternalLink size={12} /> Lihat
      </a>
    );
  }
  return (
    <div className="laksmi-dok-edit">
      <input
        className={err ? "laksmi-input-invalid" : ""}
        value={draft}
        placeholder="Tempel link publik, Enter"
        disabled={saving}
        data-testid={`laksmi-history-dok-input-${guest.guest_id}`}
        onChange={(e) => { setDraft(e.target.value); setErr(""); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
      />
      {saving && <em className="laksmi-link-checking"><Loader2 size={12} className="laksmi-spin" /> Memeriksa…</em>}
      {err && <em className="laksmi-link-bad" data-testid={`laksmi-history-dok-err-${guest.guest_id}`}>✕ {err}</em>}
    </div>
  );
};

export default function LaksmiHistory() {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/laksmi/admin/history");
    const days = res.data.days || [];
    const flat = [];
    days.forEach((day) => (day.guests || []).forEach((g) => flat.push({ ...g, date: day.date })));
    setRows(flat);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!rows) return <div className="laksmi-page"><Loader2 className="laksmi-spin" /></div>;

  return (
    <div className="laksmi-page laksmi-history laksmi-history-wide" data-testid="laksmi-history-page">
      <div className="laksmi-admin-head">
        <h1>Riwayat Tamu LAKSMI</h1>
        <span className="laksmi-pill open"><UserCheck size={13} /> {rows.length} tamu dilayani</span>
      </div>

      {rows.length === 0 ? (
        <div className="laksmi-empty" data-testid="laksmi-history-empty">Belum ada tamu yang selesai dilayani.</div>
      ) : (
        <div className="laksmi-table-wrap" data-testid="laksmi-history-table">
          <table className="laksmi-table">
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Jam</th>
                <th>Nama Tamu</th>
                <th>Jabatan</th>
                <th>Nama Zoom</th>
                <th>Asal PT</th>
                <th>Hal Konsultasi</th>
                <th>Dok Identitas</th>
                <th>Dok Instansi</th>
                <th>Dokumentasi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.guest_id} data-testid={`laksmi-history-guest-${g.guest_id}`}>
                  <td data-testid={`laksmi-history-date-${g.guest_id}`}>{fmtDate(g.date)}</td>
                  <td data-testid={`laksmi-history-jam-${g.guest_id}`}>{fmtTime(g.joined_at)}</td>
                  <td className="laksmi-td-name">{g.nama}</td>
                  <td>{g.jabatan || "-"}</td>
                  <td>{g.nama_zoom}</td>
                  <td>{g.nama_pt || "-"}</td>
                  <td className="laksmi-td-topic">{g.topik}</td>
                  <td><DocCell isLogin={g.is_login} url={g.doc_identitas_link} testid={`laksmi-history-dokid-${g.guest_id}`} /></td>
                  <td><DocCell isLogin={g.is_login} url={g.doc_pendukung_link} testid={`laksmi-history-dokinst-${g.guest_id}`} /></td>
                  <td><DokumentasiCell guest={g} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
